import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { adminApi } from "@/lib/api";
import { formatApiError } from "@/admin/AdminAuthContext";
import { PageHeader, LoadingRow, Field, FieldTextarea } from "@/admin/pages/AdminSubsidiaries";

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get("/site-settings")
      .then(({ data }) => setForm(data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const { data } = await adminApi.patch("/admin/site-settings", form);
      setForm(data);
      toast.success("Settings saved.");
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  if (loading || !form) return <><PageHeader title="Site Settings" /><LoadingRow /></>;

  const social = form.social_links || {};

  return (
    <div data-testid="admin-settings-page">
      <PageHeader title="Site Settings" />
      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white border border-softgray p-6 space-y-4">
          <h2 className="font-serif text-navy text-xl mb-2">Company</h2>
          <Field label="Company name" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} />
          <Field label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} />
          <FieldTextarea label="Address" rows={2} value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <Field label="WhatsApp number (digits only)" value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} />
          <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Field label="Website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} />
          <Field label="Working hours" value={form.working_hours} onChange={(v) => setForm({ ...form, working_hours: v })} />
        </section>

        <section className="bg-white border border-softgray p-6 space-y-4">
          <h2 className="font-serif text-navy text-xl mb-2">Map & Media</h2>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Map latitude" type="number" value={form.map_lat} onChange={(v) => setForm({ ...form, map_lat: parseFloat(v) })} />
            <Field label="Map longitude" type="number" value={form.map_lng} onChange={(v) => setForm({ ...form, map_lng: parseFloat(v) })} />
          </div>
          <Field label="Custom Google Maps embed URL (optional)" value={form.map_embed_url}
                 onChange={(v) => setForm({ ...form, map_embed_url: v })} />
          <Field label="Hero background image path (/api/media/…)" value={form.hero_background_image}
                 onChange={(v) => setForm({ ...form, hero_background_image: v })} />

          <h2 className="font-serif text-navy text-xl pt-6 border-t border-softgray">Social links</h2>
          {["linkedin", "twitter", "facebook", "instagram", "youtube"].map((k) => (
            <Field key={k} label={k[0].toUpperCase() + k.slice(1)} value={social[k] || ""}
                   onChange={(v) => setForm({ ...form, social_links: { ...social, [k]: v } })} />
          ))}
        </section>
      </div>
      <div className="mt-8">
        <button onClick={save} disabled={saving} className="btn-gold" data-testid="save-settings">
          <Save size={14} /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}
